import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, SlidersHorizontal } from "lucide-react";
import { useSearchParams, Link } from "react-router-dom";
import toast from "react-hot-toast";

import { productService } from "../services/productService";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BannerPage from "../components/BannerPage";
import WhatsAppFloat from "../components/WhatsAppFloat";
import WelcomeModal from "../components/WelcomeModal";
import Login from "./Login"; // Fixed path: since they are in the same folder
import { useAuth } from "../context/AuthContext";

const PRODUCTS_PER_PAGE = 18; 
const CATEGORIES_PER_PAGE = 18;

const services = [
  { name: "Desktop Service", image: "https://res.cloudinary.com/pronest/image/upload/v1768811534/Desktop_jl0z5g.jpg", path: "/Desktop-service" },
  { name: "Laptop Service", image: "https://res.cloudinary.com/pronest/image/upload/v1768839208/surface-laptop-16-9_fvwwrs.jpg", path: "/Laptop-service" },
  { name: "Printer Service", image: "https://res.cloudinary.com/pronest/image/upload/v1768811534/Printer_gpbvny.jpg", path: "/Printer-service" },
  { name: "CCTV Service", image: "https://res.cloudinary.com/pronest/image/upload/v1768811533/CCTV_uh7cb9.jpg", path: "/CCTV-service" },
  { name: "Electrical Service", image: "https://res.cloudinary.com/pronest/image/upload/v1768811537/Electrical_wzs1xb.jpg", path: "/Electrical-service" },
  { name: "Plumbing Service", image: "https://res.cloudinary.com/pronest/image/upload/v1768811533/Plumbing_kpbyni.jpg", path: "/Plumbing-service" }
];

const Store = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [showQuickView, setShowQuickView] = useState(null);
  const [activeTab, setActiveTab] = useState("products");
  const [animateKey, setAnimateKey] = useState(0);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [productCondition, setProductCondition] = useState(null);
  
  // Track state control over modal pop-up status
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  
  useEffect(() => {
    // Only auto-open login modal if user is NOT authenticated and hasn't dismissed it this session
    if (!user && !sessionStorage.getItem("store_login_prompt_shown")) {
      setIsLoginOpen(true);
      sessionStorage.setItem("store_login_prompt_shown", "true");
    }
  }, [user]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([productService.getAllProducts(), productService.getCategories()]);
        setProducts(prodRes.data);
        setCategories(["All", ...catRes.data]);
      } catch {
        toast.error("Failed to load store data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const category = searchParams.get("category");
    const search = searchParams.get("search") || "";
    const sort = searchParams.get("sort") || "";
    setSelectedCategory(category);
    setSearchTerm(search);
    setSortBy(sort);
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      let temp = [...products];

      if (productCondition) temp = temp.filter((p) => p.condition === productCondition);
      if (selectedCategory && selectedCategory !== "All") temp = temp.filter((p) => p.category === selectedCategory);
      if (searchTerm) {
        temp = temp.filter((p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (sortBy === "priceLow") temp.sort((a, b) => a.price - b.price);
      if (sortBy === "priceHigh") temp.sort((a, b) => b.price - a.price);
      if (sortBy === "newest") temp.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setFilteredProducts(temp);
      setCurrentPage(1);

      setSearchParams({
        ...(selectedCategory && { category: selectedCategory }),
        ...(searchTerm && { search: searchTerm }),
        ...(sortBy && { sort: sortBy })
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [products, selectedCategory, searchTerm, sortBy, productCondition, setSearchParams]);

  const currentProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const categoryCards = Object.values(
    products.reduce((acc, p) => {
      if (!acc[p.category]) acc[p.category] = { name: p.category, image: p.image || p.images?.[0] };
      return acc;
    }, {})
  );

  const totalCategoryPages = Math.ceil(categoryCards.length / CATEGORIES_PER_PAGE);
  const paginatedCategories = useMemo(() => {
    const start = (categoryPage - 1) * CATEGORIES_PER_PAGE;
    return categoryCards.slice(start, start + CATEGORIES_PER_PAGE);
  }, [categoryCards, categoryPage]);

  const refurbishedCategoryCards = Object.values(
    products
      .filter((p) => p.condition === "refurbished")
      .reduce((acc, p) => {
        if (!acc[p.category]) acc[p.category] = { name: p.category, image: p.image || p.images?.[0] };
        return acc;
      }, {})
  );

  const totalRefurbishedPages = Math.ceil(refurbishedCategoryCards.length / PRODUCTS_PER_PAGE);
  const currentRefurbished = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return refurbishedCategoryCards.slice(start, start + PRODUCTS_PER_PAGE);
  }, [refurbishedCategoryCards, currentPage]);

  // Modern Centered Loading Layout with cleanly positioned logo placeholder
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50/60 antialiased px-4">
        <div className="relative flex items-center justify-center h-28 w-28">
          {/* Outer Spin Ring */}
          <div className="absolute inset-0 rounded-full border-[2.5px] border-slate-200 border-t-purple-600 animate-spin" />
          
          {/* Centered Logo Mask */}
          <div className="h-24 w-24 rounded-full bg-white flex items-center justify-center border border-slate-100 shadow-xs overflow-hidden animate-pulse-slow">
            <img 
              src="/images/pronest_logo.png" 
              alt="Pronest Logo" 
              className="h-16 w-16 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }} 
            />
          </div>
        </div>
        <span className="mt-5 text-[11px] font-bold text-slate-500 uppercase tracking-[0.16em] opacity-80 animate-pulse">
          Loading Storefront
        </span>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 antialiased">
      <Header />
      <WhatsAppFloat />

      <main className="flex-1 pb-20">
        <BannerPage
          categoryCards={categoryCards}
          onCategoryClick={(category) => {
            setActiveTab("products");
            setSelectedCategory(category);
            setSearchTerm("");
            setSortBy("");
            setAnimateKey((k) => k + 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />

        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          
          {/* Tabs Navigation Layout Container */}
          <div className="mb-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="inline-flex rounded-xl bg-slate-100 p-1">
              {["products", "services", "refurbished"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setSelectedCategory(null);
                    setSearchTerm("");
                    setSortBy("");
                    setAnimateKey((k) => k + 1);
                    setProductCondition(tab === "refurbished" ? "refurbished" : null);
                    setCurrentPage(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold capitalize transition-all duration-200 sm:px-4 ${
                    activeTab === tab
                      ? "bg-white text-slate-900 shadow-xs"
                      : "text-slate-500 hover:bg-white/70 hover:text-slate-800"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "products" && (
            <div key={animateKey} className="animate-fade-slide">
              {!selectedCategory ? (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3.5">
                    {paginatedCategories.map((cat) => {
                      const active = selectedCategory === cat.name;
                      return (
                        <button
                          key={cat.name}
                          onClick={() => {
                            setSelectedCategory(cat.name);
                            setAnimateKey((k) => k + 1);
                          }}
                          className={`group rounded-lg border p-1 text-center transition-all duration-150 bg-white hover:border-slate-300 ${active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200/70'}`}
                        >
                          <div className="mb-1 aspect-square overflow-hidden rounded-md bg-slate-50/50 p-2">
                            <img src={cat.image} alt={cat.name} className="w-full h-full object-contain mix-blend-multiply transition duration-200 group-hover:scale-105" />
                          </div>
                          <h4 className="truncate px-0.5 text-[10px] font-medium text-slate-700 leading-tight group-hover:text-slate-900">{cat.name}</h4>
                        </button>
                      );
                    })}
                  </div>

                  {totalCategoryPages > 1 && (
                    <div className="mt-4 mb-1 flex items-center justify-center gap-4 border-t border-slate-100 pt-6">
                      <button 
                        onClick={() => setCategoryPage((prev) => Math.max(prev - 1, 1))} 
                        disabled={categoryPage === 1} 
                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition hover:bg-slate-300"
                      >
                        Previous
                      </button>
                      <span className="text-xs font-semibold text-slate-500 tracking-wider">
                        {categoryPage} <span className="text-slate-300 mx-1">/</span> {totalCategoryPages}
                      </span>
                      <button 
                        onClick={() => setCategoryPage((prev) => (prev < totalCategoryPages ? prev + 1 : prev))} 
                        disabled={categoryPage === totalCategoryPages} 
                        className="rounded-lg border border-transparent bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition hover:bg-purple-700"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-1.5 flex items-center justify-between">
                    <button onClick={() => { setSelectedCategory(null); setCategoryPage(1); }} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 transition">
                      <ChevronLeft size={14} />
                      Back to categories
                    </button>
                  </div>

                  <div className="mb-1.5 flex gap-2">
                    <input
                      type="text"
                      placeholder={`Search in ${selectedCategory}`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-slate-400"
                    />

                    <div className="relative">
                      <button
                        onClick={() => setShowSortMenu((prev) => !prev)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                      </button>

                      {showSortMenu && (
                        <div className="absolute right-0 z-20 mt-1 w-36 rounded-lg border border-slate-200 bg-white p-0.5 shadow-lg ring-1 ring-black/5">
                          <button onClick={() => { setSortBy("priceLow"); setShowSortMenu(false); }} className="block w-full rounded-md px-2 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50">Price: Low → High</button>
                          <button onClick={() => { setSortBy("priceHigh"); setShowSortMenu(false); }} className="block w-full rounded-md px-2 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50">Price: High → Low</button>
                          <button onClick={() => { setSortBy("newest"); setShowSortMenu(false); }} className="block w-full rounded-md px-2 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50">Newest</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {currentProducts.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-white py-6 text-center text-xs text-slate-400">No products found.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 custom-product-zoom">
                      {currentProducts.map((p) => <ProductCard key={p._id} product={p} onQuickView={setShowQuickView} />)}
                    </div>
                  )}

                  {filteredProducts.length > PRODUCTS_PER_PAGE && (
                    <div className="mt-8 mb-4 flex items-center justify-center gap-4 border-t border-slate-100 pt-6">
                      <button 
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} 
                        disabled={currentPage === 1} 
                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition hover:bg-slate-300"
                      >
                        Previous
                      </button>
                      <span className="text-xs font-semibold text-slate-500 tracking-wider">
                        {currentPage} <span className="text-slate-300 mx-1">/</span> {Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)}
                      </span>
                      <button 
                        onClick={() => setCurrentPage((prev) => (prev < Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE) ? prev + 1 : prev))} 
                        disabled={currentPage === Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)} 
                        className="rounded-lg border border-transparent bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "services" && (
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3.5">
              {services.map((service, index) => (
                <Link key={index} to={service.path} className="group rounded-lg border border-slate-200/70 bg-white p-1 text-center transition-all duration-150 hover:border-slate-300">
                  <div className="mb-1 aspect-square overflow-hidden rounded-md bg-slate-50/50 p-2">
                    <img src={service.image} alt={service.name} className="h-full w-full object-contain transition duration-200 group-hover:scale-105" />
                  </div>
                  <h3 className="truncate px-0.5 text-[10px] font-medium text-slate-700 leading-tight group-hover:text-slate-900">{service.name}</h3>
                </Link>
              ))}
            </div>
          )}

          {activeTab === "refurbished" && (
            <div className="animate-fade-slide">
              {currentRefurbished.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white py-6 text-center text-xs text-slate-400">No products available.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3.5">
                  {currentRefurbished.map((cat) => (
                    <button key={cat.name} onClick={() => { setSelectedCategory(cat.name); setActiveTab("products"); setProductCondition("refurbished"); setAnimateKey((k) => k + 1); }} className="group rounded-lg border border-slate-200/70 bg-white p-1 text-center transition-all duration-150 hover:border-slate-300">
                      <div className="mb-1 aspect-square overflow-hidden rounded-md bg-slate-50/50 p-2">
                        <img src={cat.image} alt={cat.name} className="h-full w-full object-contain" />
                      </div>
                      <h3 className="truncate px-0.5 text-[10px] font-medium text-slate-700 leading-tight group-hover:text-slate-900">{cat.name}</h3>
                    </button>
                  ))}
                </div>
              )}

              {refurbishedCategoryCards.length > PRODUCTS_PER_PAGE && (
                <div className="mt-8 mb-4 flex items-center justify-center gap-4 border-t border-slate-100 pt-6">
                  <button 
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} 
                    disabled={currentPage === 1} 
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition hover:bg-slate-300"
                  >
                    Previous
                  </button>
                  <span className="text-xs font-semibold text-slate-500 tracking-wider">
                    {currentPage} <span className="text-slate-300 mx-1">/</span> {totalRefurbishedPages}
                  </span>
                  <button 
                    onClick={() => setCurrentPage((prev) => (prev < totalRefurbishedPages ? prev + 1 : prev))} 
                    disabled={currentPage === totalRefurbishedPages} 
                    className="rounded-lg border border-transparent bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {showQuickView && <ProductModal product={showQuickView} onClose={() => setShowQuickView(null)} />}
      
      {/* Login modal rendered dynamically */}
      <Login isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />

      <Footer />

      <style>{`
        .animate-fade-slide {
          animation: fadeSlide 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(3px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-pulse-slow {
          animation: pulseSlow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulseSlow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(0.96); }
        }
        .custom-product-zoom img {
          transform: scale(0.9);
          transition: transform 0.2s ease-in-out;
        }
        .custom-product-zoom button:hover img {
          transform: scale(0.95);
        }
      `}</style>
    </div>
  );
};

export default Store;